import type { mat4 } from 'gl-matrix';

import type { Ref } from 'vue';

import type { Series } from '@/src/store/dicom-web/current-study-store';

// src/composables/useLocalizerGeometry.ts
import { computed } from 'vue';

import { image2DCordinateToWorld } from '@/src/components/pixel-overlay/coordinateTransformation';
import { calculateLocalizer } from '@/src/components/vtkTwoView-utils-localizer';
import { useContourContainerStore } from '@/src/store/contours/contour-container-store';
import { ContourLabel } from '@/src/store/contours/contour-definitions';
import { useViewportStateStore } from '@/src/store/contours/viewport-state-store';
import { useCurrentStudyStore } from '@/src/store/dicom-web/current-study-store';

export type Vec3 = [number, number, number];

export interface LocalizerGeometry { p1: Vec3; p2: Vec3; dir: Vec3 }

export function normalizeVec3(v: Vec3): Vec3 | null {
  const mag = Math.hypot(v[0], v[1], v[2]);
  if (mag < 1e-6) return null;
  return [v[0] / mag, v[1] / mag, v[2] / mag];
}

export function useLocalizerGeometry(
  viewID: Ref<string>,
  currentImageID: Ref<string>,
  indexToWorld: Ref<mat4>,
) {
  const currentStudyStore = useCurrentStudyStore();
  const viewportStateStore = useViewportStateStore();

  const localizerGeometry = computed<LocalizerGeometry | null>(() => {
    const itw = indexToWorld.value;
    if (!itw) return null;
    const refUid = viewportStateStore.getActiveReferenceImageUid(viewID.value);
    const dst = currentStudyStore.$state.imageInfoDicom[currentImageID.value];
    const src = refUid ? currentStudyStore.$state.imageInfoDicom[refUid] : null;
    if (!dst || !src) return null;

    const { intersectionLinePoints } = calculateLocalizer(
      dst.ImageOrientationPatient,
      dst.ImagePositionPatient,
      dst.PixelSpacing[0],
      dst.PixelSpacing[1],
      dst.Rows,
      dst.Columns,
      src.ImageOrientationPatient,
      src.ImagePositionPatient,
      src.PixelSpacing[0],
      src.PixelSpacing[1],
      src.Rows,
      src.Columns,
    );
    if (!intersectionLinePoints) return null;

    const p1 = image2DCordinateToWorld(intersectionLinePoints[0][0], intersectionLinePoints[0][1], itw);
    const p2 = image2DCordinateToWorld(intersectionLinePoints[1][0], intersectionLinePoints[1][1], itw);
    const dir = normalizeVec3([p2[0] - p1[0], p2[1] - p1[1], p2[2] - p1[2]]);
    return dir ? { p1, p2, dir } : null;
  });

  return { localizerGeometry };
}

function dot3(a: number[], b: number[]) {
  return a[0] * b[0] + a[1] * b[1] + a[2] * b[2];
}
function cross3(a: number[], b: number[]): [number, number, number] {
  return [
    a[1] * b[2] - a[2] * b[1],
    a[2] * b[0] - a[0] * b[2],
    a[0] * b[1] - a[1] * b[0],
  ];
}

// Interval [lo, hi] along `n` for the range on one LAX phase image, or null.
function rangeIntervalForLaxImage(
  laxUid: string | undefined,
  n: number[],
): [number, number] | null {
  const currentStudyStore = useCurrentStudyStore();
  const contourStore = useContourContainerStore();
  if (!laxUid) return null;

  const range = contourStore.getContourOfLabel(laxUid, ContourLabel.LaxLvRange)?.[0];
  const itw = currentStudyStore.getVtkMetaData(laxUid)?.indexToWorld;
  if (!range || range.points.length < 2 || !itw) return null;

  const a = range.points[0];
  const b = range.points[range.points.length - 1];
  const aW = image2DCordinateToWorld(a.x, a.y, itw);
  const bW = image2DCordinateToWorld(b.x, b.y, itw);
  const sA = dot3(n, aW);
  const sB = dot3(n, bW);
  return [Math.min(sA, sB), Math.max(sA, sB)];
}

// [phase] -> interval along the SAX normal, or null (= no restriction that phase).
export function computeSaxRangeIntervals(
  saxSeries: Series,
  laxSeriesIdx: number,
  laxSliceIdx: number,
  saxPhaseCount: number,
): Array<[number, number] | null> {
  const currentStudyStore = useCurrentStudyStore();
  const none = () =>
    Array.from<[number, number] | null>({ length: saxPhaseCount }).fill(null);

  // SAX normal (single parallel stack).
  const anySax = saxSeries.slices[0]?.images[0]?.uid;
  const iop = anySax
    ? currentStudyStore.imageInfoDicom[anySax]?.ImageOrientationPatient
    : null;
  if (!iop) return none();
  const n = cross3(iop.slice(0, 3), iop.slice(3, 6));

  // Active-secondary LAX images (one per LAX phase).
  const laxUids = currentStudyStore.getSliceImageUIDs(laxSeriesIdx, laxSliceIdx);
  if (!laxUids || laxUids.length === 0) return none();
  const laxPhaseCount = laxUids.length;

  // Different phase counts -> single range: the first LAX phase that has one,
  // applied to every SAX phase.
  if (laxPhaseCount !== saxPhaseCount) {
    let iv: [number, number] | null = null;
    for (let p = 0; p < laxPhaseCount && !iv; p++) {
      iv = rangeIntervalForLaxImage(laxUids[p], n);
    }
    return Array.from<[number, number] | null>({ length: saxPhaseCount }).fill(iv);
  }

  // Equal phase counts -> per-phase; a phase with no range = null (unrestricted).
  return Array.from({ length: saxPhaseCount }, (_, p) =>
    rangeIntervalForLaxImage(laxUids[p], n));
}
